import React, { useState } from 'react';
import { FaCalendar, FaClock, FaUsers, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { supabase } from '../../utils/supabase/client';
import { supabaseFreeTable } from '../../utils/supabase/clientFreeTable';
import { useAuth } from '../../api/AuthProvider';
import { useQueryClient } from '@tanstack/react-query';
import { sendEmail } from '../../api/emails';
import { useRestaurant } from '../../api/restaurant';

// Guest Details Modal
const GuestDetailsModal = ({ isOpen, onClose, guestDetails }) => {
  const { profile } = useAuth();
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          width: '100%',
          maxWidth: '450px',
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          transform: 'translateY(0)',
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        <button
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            color: '#3eb488',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            transition: 'color 0.2s ease-in-out',
          }}
          onClick={onClose}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#2a9066')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#3eb488')}
        >
          <FaTimes size={32} />
        </button>
        <h2
          style={{
            marginBottom: '16px',
            fontSize: '28px',
            fontWeight: '700',
            color: '#3eb488',
            textAlign: 'center',
            letterSpacing: '0.5px',
          }}
        >
          Детали за гостинот
        </h2>
        <p
          style={{
            fontSize: '18px',
            color: '#333',
            marginBottom: '12px',
          }}
        >
          <strong style={{ fontWeight: '600' }}>Име:</strong> {guestDetails.fullName || 'N/A'}
        </p>
        <p
          style={{
            fontSize: '18px',
            color: '#333',
            marginBottom: '12px',
          }}
        >
          <strong style={{ fontWeight: '600' }}>Телефонски број:</strong>{' '}
          {guestDetails.phoneNumber || 'N/A'}
        </p>
        <p
          style={{
            fontSize: '18px',
            color: '#333',
            marginBottom: '12px',
          }}
        >
          <strong style={{ fontWeight: '600' }}>Email:</strong> {guestDetails.email || 'N/A'}
        </p>
        <button
          style={{
            marginTop: '20px',
            backgroundColor: '#3eb488',
            color: '#ffffff',
            padding: '16px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            width: '100%',
            fontSize: '18px',
            fontWeight: '600',
            transition: 'background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
          }}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2a9066';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#3eb488';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          Затвори
        </button>
      </div>
    </div>
  );
};

const UnconfirmedReservationCard = ({
  reservation,
  tempPartySize,
  handleConfirm,
  handleDeny,
}) => {
  const { profile } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: restaurantData, isLoading: isRestaurantLoading } = useRestaurant();
  const [isLoading, setIsLoading] = useState(false);

  const isMKD = reservation.language === "MK";
  const isDarkMode = restaurantData?.isDarkMode;

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`;
  };

  const formatTimeForDisplay = (timeString) => {
    if (!timeString) return 'N/A';
    const [hour, minute] = timeString.split(':');
    if (!hour || !minute || isNaN(parseInt(hour)) || isNaN(parseInt(minute))) return 'Invalid Time';
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  };

  const body = `<div style="font-family: Arial, Helvetica, sans-serif; margin: 20px auto; max-width: 600px; text-align: center; background-color: ${isDarkMode ? '#000000' : '#FFFFFF'}; border-radius: 12px; padding: 20px;">
            <img src="${restaurantData?.mainImage || 'https://qglldlnkkypojvppmqzs.supabase.co/storage/v1/object/public/restoranisliki/freetablesidenobg.png'}" alt="${restaurantData?.name || 'Restaurant'} Logo" style="max-width: 200px; height: auto; border-radius: 10px; display: inline-block;">
            <p style="font-size: 24px; font-weight: 600; margin: 20px 0; color: ${restaurantData?.mainColor}; line-height: 1.5;">
              ${isMKD ? 'Потврдена резервација' : 'Reservation Confirmed'}
            </p>
            <p style="font-size: 16px; margin-bottom: 20px; color: ${isDarkMode ? '#E0E0E0' : '#212121'}; line-height: 1.5;">
              ${isMKD ? 'Здраво' : 'Hello'} ${reservation.fullName},
            </p>
            <p style="font-size: 16px; margin-bottom: 20px; color: ${isDarkMode ? '#E0E0E0' : '#212121'}; line-height: 1.5;">
              ${isMKD ? `Вашата резервација во ${restaurantData?.name} е потврдена!` : `Your reservation at ${restaurantData?.name} has been confirmed!`}
            </p>
            <div style="text-align: center; margin-bottom: 20px; background-color: ${isDarkMode ? '#2C2C2C' : '#F5F5F5'}; padding: 15px; border-radius: 8px;">
              <p style="font-size: 16px; margin: 0 0 10px; color: ${isDarkMode ? '#E0E0E0' : '#212121'}; line-height: 1.5;">
                ${isMKD ? 'Детали за резервацијата' : 'Reservation Details'}:
              </p>
              <ul style="list-style: none; padding: 0; font-size: 16px; margin: 0; color: ${isDarkMode ? '#E0E0E0' : '#212121'}; line-height: 1.5;">
                <li style="margin-bottom: 10px;">${isMKD ? 'Датум' : 'Date'}: ${formatDateForDisplay(reservation.date)}</li>
                <li style="margin-bottom: 10px;">${isMKD ? 'Време' : 'Time'}: ${formatTimeForDisplay(reservation.time)}</li>
                <li style="margin-bottom: 10px;">${isMKD ? 'Број на луѓе' : 'Party Size'}: ${reservation.people}</li>
                <li style="margin-bottom: 10px;">${isMKD ? 'Место' : 'Location'}: ${reservation.seatingLocation || 'N/A'}</li>
                ${reservation.additionalRequest ? `<li style="margin-bottom: 10px;">${isMKD ? 'Дополнително барање' : 'Additional Request'}: ${reservation.additionalRequest}</li>` : ''}
              </ul>
            </div>
            <p style="font-size: 16px; margin-bottom: 20px; color: ${isDarkMode ? '#E0E0E0' : '#212121'}; line-height: 1.5;">
              ${isMKD ? `Ви благодариме што го избравте ${restaurantData?.name}!` : `Thank you for choosing ${restaurantData?.name}!`}
            </p>
            <div style="text-align: center; margin-bottom: 20px;">
              <a href="https://reserve.freetable.mk/details?id=${reservation.restaurantId}" style="background-color: ${restaurantData?.mainColor}; color: ${isDarkMode ? '#000000' : '#FFFFFF'}; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-size: 16px; font-weight: 500; display: inline-block;">
                ${isMKD ? 'Погледнете детали за ресторанот' : 'View Restaurant Details'}
              </a>
            </div>
            <div style="text-align: center; margin-bottom: 20px;">
              <a href="https://reserve.freetable.mk/EditScreen?id=${reservation.restaurantId}&language=${reservation.language}&reservationId=${reservation.cancelationId}" style="background-color: ${restaurantData?.secondaryColor}; color: ${isDarkMode ? '#E0E0E0' : '#FFFFFF'}; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-size: 16px; font-weight: 500; display: inline-block;">
                ${isMKD ? 'Откажи резервација' : 'Cancel Reservation'}
              </a>
            </div>
            <p style="font-size: 16px; margin-bottom: 20px; color: ${isDarkMode ? '#E0E0E0' : '#212121'}; line-height: 1.5;">
              ${isMKD ? 'Ви благодариме што резервиравте со Free Table!' : 'Thank you for reserving with Free Table!'}
            </p>
            <div style="text-align: center;">
              <p style="font-size: 14px; margin: 0 0 10px; color: ${isDarkMode ? '#E0E0E0' : '#212121'}; line-height: 1.5;">Powered by:</p>
              <img src="https://qglldlnkkypojvppmqzs.supabase.co/storage/v1/object/public/restoranisliki/freetablesidenobg.png" alt="Free Table Logo" style="width: 150px; height: auto; display: inline-block;">
            </div>
          </div>`;

  const handleSubmit = async (status) => {
    try {
      if (!reservation.phoneNumber && !reservation.email) {
        console.error('Guest must have a phone number or email address.');
        return;
      }
      if (isLoading) {
        return;
      }
      setIsLoading(true);

      // Check if customer exists
      const { data: existingCustomer, error: customerError } = await supabase
        .from('customers')
        .select('id, reservations')
        .eq('phone', reservation.phoneNumber)
        .eq('email', reservation.email)
        .eq('name', reservation.fullName)
        .eq('restaurant_id', profile.restaurantid)
        .single();

      if (customerError && customerError.code !== 'PGRST116') {
        throw new Error('Error checking customer: ' + customerError.message);
      }

      let guestId;
      if (existingCustomer) {
        guestId = existingCustomer.id;
        const { error: updateError } = await supabase
          .from('customers')
          .update({ reservations: existingCustomer.reservations + 1 })
          .eq('id', guestId);

        if (updateError) {
          throw new Error('Error updating customer reservations: ' + updateError.message);
        }
      } else {
        const { data: newCustomer, error: createError } = await supabase
          .from('customers')
          .insert([
            {
              phone: reservation.phoneNumber,
              email: reservation.email || null,
              name: reservation.fullName || null,
              restaurant_id: profile.restaurantid,
              reservations: 1,
            },
          ])
          .select('id')
          .single();

        if (createError) {
          throw new Error('Error creating customer: ' + createError.message);
        }
        guestId = newCustomer.id;
      }

      // Create reservation
      const reservationDateTime = new Date(`${reservation.date}T${reservation.time}`);
      const reservationData = {
        guest_id: guestId,
        name: reservation.fullName,
        partySize: parseInt(reservation.people),
        time: reservationDateTime.toISOString(),
        restaurantid: profile.restaurantid,
        tableIds: [],
        prefered_place: reservation.seatingLocation,
        language: reservation.language,
        additionalRequest: reservation.additionalRequest,
        cancelationId: reservation.cancelationId,
        confirmedBy: profile.full_name,
        status: status == "CONFIRMED"?"pending":status
      };

      const { error: reservationError } = await supabase
        .from('reservations')
        .insert([reservationData]);

      if (reservationError) {
        throw new Error('Error creating reservation: ' + reservationError.message);
      }

      // Update unconfirmed reservation status
      const { error: updateError } = await supabaseFreeTable
        .from('unconfirmedReservations')
        .update({ status: status })
        .eq('id', reservation.id);

      if (updateError) {
        throw new Error('Error updating unconfirmed reservation: ' + updateError.message);
      }

      // Send email notification
      if (reservation.email) {

        if(status === "CONFIRMED"){
          const { data, error } = await supabaseFreeTable.functions.invoke('emailSender', {
      body: {
        email: reservation.email,
        subject: isMKD?"Потврдена Резервација":"Confirmed Reservation",
        body: body,
      },
    });

        }else{
          const placeholders = {
          'Guest Name': reservation.fullName || 'Guest',
          'Restaurant Name': restaurantData?.name || 'Our Restaurant',
          'Reservation Date': formatDateForDisplay(reservation.date),
          'Reservation Time': formatTimeForDisplay(reservation.time),
          'Number of Guests': reservation.people,
          'Restaurant Address': restaurantData?.location || 'Unknown Address',
          'Contact Info': restaurantData?.contactInfo || 'contact@freetable.mk',
        };

        
        

        const sendResult = await sendEmail({
          restaurantId: profile.restaurantid,
          templateKey: status === 'declined',
          language: reservation.language,
          recipient: reservation.email,
          placeholders,
        });

        if (!sendResult.success) {
          console.warn(`Failed to send ${status.toLowerCase()} email:`, sendResult.error);
        }
      }
    }

      queryClient.invalidateQueries(['reservations', 'unconfirmedFreeTable']);
      queryClient.invalidateQueries(['reservations']);
      setIsLoading(false);
    } catch (err) {
      console.error(`Error in handleSubmit (${status}):`, err.message);
      setIsLoading(false);
    }
  };

  const confirmReservation = async () => {
    try {
      await handleSubmit('CONFIRMED');
      handleConfirm(reservation.id);
    } catch (err) {
      console.error('Error confirming reservation:', err.message);
    }
  };

  const declineReservation = async () => {
    try {
      await handleSubmit('DECLINED');
      handleDeny(reservation.id);
    } catch (err) {
      console.error('Error declining reservation:', err.message);
    }
  };

  const convertDateToShow = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const convertTimeToShow = (time) => {
    if (!time) return "Избери време";
    const [hour, minute] = time.split(':');
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  };

  const guestDisplay = reservation.fullName || 'Unknown';

  if (isRestaurantLoading) {
    return (
      <div
        style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#333',
          padding: '20px',
        }}
      >
        Loading restaurant data...
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          width: '90%',
          maxWidth: '600px',
          borderRadius: '12px',
          margin: '20px auto',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          padding: '16px',
          transition: 'transform 0.2s ease-in-out',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      >
        {/* Header: Guest Name */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
          }}
        >
          <button
            style={{
              fontSize: '22px',
              margin: '0 0 0 16px',
              color: '#3eb488',
              fontWeight: '700',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'color 0.2s ease-in-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#2a9066')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#3eb488')}
          >
            {guestDisplay}
          </button>
        </div>

        {/* Discount Banner */}
        {restaurantData.discount > 0 && (
          <div
            style={{
              textAlign: 'center',
              backgroundColor: '#3eb488',
              padding: '12px',
              color: '#ffffff',
              fontWeight: '600',
              width: '95%',
              margin: '8px auto',
              borderRadius: '10px',
              fontSize: '16px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            Оваа резервација има {restaurantData.discount}% попуст
          </div>
        )}

        {/* Info Section */}
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaCalendar size={28} color="#3eb488" />
              <div style={{ marginLeft: '8px' }}>
                <span style={{ fontWeight: '500', fontSize: '16px', color: '#333' }}>
                  {convertDateToShow(reservation.date)}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaClock size={28} color="#3eb488" />
              <div style={{ marginLeft: '8px' }}>
                <span style={{ fontWeight: '500', fontSize: '16px', color: '#333' }}>
                  {formatTimeForDisplay(reservation.time)}
                </span>
                
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', padding: '0 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaUsers size={28} color="#3eb488" />
              <div style={{ marginLeft: '8px' }}>
                <span style={{ fontWeight: '500', fontSize: '16px', color: '#333' }}>
                  {reservation.people || 0}
                </span>
                
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaMapMarkerAlt size={28} color="#3eb488" />
              <div style={{ marginLeft: '8px' }}>
                <span style={{ fontWeight: '500', fontSize: '16px', color: '#333' }}>
                  {reservation.seatingLocation || 'N/A'}
                </span>
                
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        {reservation.additionalRequest && (
          <>
            <div
              style={{
                borderBottom: '2px solid #3eb488',
                paddingBottom: '8px',
                margin: '16px auto 8px auto',
                width: '85%',
              }}
            >
              <span
                style={{
                  color: '#3eb488',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '22px',
                  display: 'block',
                }}
              >
                Дополнително барање
              </span>
            </div>
            <span
              style={{
                textAlign: 'center',
                fontSize: '18px',
                width: '90%',
                display: 'block',
                margin: '0 auto',
                color: '#333',
              }}
            >
              {reservation.additionalRequest}
            </span>
          </>
        )}

        {/* Confirm and Decline Buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '16px 0',
            gap: '12px',
          }}
        >
          <button
            style={{
              backgroundColor: '#e74c3c',
              padding: '12px',
              borderRadius: '8px',
              width: '140px',
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              declineReservation();
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#c0392b')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#e74c3c')}
          >
            {isLoading ? 'Се вчитува...' : 'Одбиј'}
          </button>
          <button
            style={{
              backgroundColor: '#3eb488',
              padding: '12px',
              borderRadius: '8px',
              width: '140px',
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              confirmReservation();
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2a9066')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#3eb488')}
          >
            {isLoading ? 'Се вчитува...' : 'Потврди'}
          </button>
        </div>
      </div>

      {/* Guest Details Modal */}
      <GuestDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        guestDetails={{
          fullName: reservation.fullName,
          phoneNumber: reservation.phoneNumber,
          email: reservation.email,
        }}
      />
    </>
  );
};

export default UnconfirmedReservationCard;